// SDK › Instrumentation (D114, APS-428; the mock's #/instrumentation, design/console-d/views/
// instrumentation.js, as Mosaic mapped it onto spec 3872a74): the SDK drawn as a device, a tile
// per capability the policy can change, and beside it the selected instrument's panel: its state
// and staged rollout (a layer, spec 12.4) with its holdback, targeting and guardrails, the cost
// with and without it when the API can compare them, its settings and its rollout so far. The
// kill switch (12.3) stops the whole SDK from the head. Instruments the policy cannot reach are
// not drawn; one line says the rest are set in the app.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type CSSProperties, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  instrumentationQuery,
  lastDays,
  layerComparisonQuery,
  layersQuery,
  orgQuery,
  releasesQuery,
  useSaveLayers,
  useSetKillSwitch,
  useStopLayer,
} from '../api/queries'
import type { App, Layer, LayerComparison, Platform } from '../api/types'
import { Confirm } from '../components/Confirm'
import { FAMILY_KEY } from '../components/DeviceData'
import { Btn, EmptyNote, PageHead, Section, Seg, Strip, useToast } from '../components/d'
import { Icon } from '../components/icons'
import { Locked } from '../components/Locked'
import { SandboxReadOnly } from '../components/SandboxReadOnly'
import { PLATFORM_LABEL } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { day, dayTime, percentSpaced } from '../lib/format'
import {
  activeBodies,
  HOLDBACK_MAX,
  hasSubjects,
  INSTRUMENT_GROUPS,
  INSTRUMENTS,
  type InstrumentGroup,
  type InstrumentId,
  idHint,
  instrumentsOf,
  layerOf,
  type ReleaseCell,
  ROLLOUT_STOPS,
  type RolloutDraft,
  recentReleases,
  releaseCell,
  rolloutDraft,
  type TileState,
  tileState,
  withoutSubjects,
  withRollout,
} from '../lib/instruments'
import { type Draft, resourcesSdk, toDraft } from '../lib/signals'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import type { ScopeSearch } from './search'
import { changedLine, useWho } from './useWho'

/** The SDK guide for an app's platform: where the instruments not set here are set. */
const SDK_GUIDE: Record<Platform, string> = {
  ios: 'https://docs.apsio.io/sdks/ios',
  android: 'https://docs.apsio.io/sdks/android',
  react_native: 'https://docs.apsio.io/sdks/react-native',
}

const NAME_KEY = {
  crash: 'instr.name.crash',
  errors: 'instr.name.errors',
  network: 'instr.name.network',
  spans: 'instr.name.spans',
  metrics: 'instr.name.metrics',
  replay: 'instr.name.replay',
  logs: 'instr.name.logs',
  resources: 'instr.name.resources',
} as const satisfies Record<InstrumentId, MessageId>

const WHAT_KEY = {
  crash: 'instr.what.crash',
  errors: 'instr.what.errors',
  network: 'instr.what.network',
  spans: 'instr.what.spans',
  metrics: 'instr.what.metrics',
  replay: 'instr.what.replay',
  logs: 'instr.what.logs',
  resources: 'instr.what.resources',
} as const satisfies Record<InstrumentId, MessageId>

const GROUP_KEY = {
  stability: 'instr.group.stability',
  performance: 'instr.group.performance',
  experience: 'instr.group.experience',
  context: 'instr.group.context',
} as const satisfies Record<InstrumentGroup, MessageId>

type Tab = 'rollout' | 'cost' | 'settings' | 'history'

/** The page, or in the sandbox, which cannot change anything, the reason (D64). */
export function InstrumentationPage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxReadOnly /> : <InstrumentationView />
}

function InstrumentationView() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const apps = useQuery(appsQuery(orgId, projectId))
  const org = useQuery(orgQuery(orgId))
  const search = useSearch({ strict: false }) as ScopeSearch & { instrument?: string }
  const list = apps.data ?? []
  const app = list.find((a) => a.id === search.app) ?? (list.length === 1 ? list[0] : undefined)
  const canEdit = org.data ? org.data.role === 'owner' || org.data.role === 'admin' : undefined
  const title = t('instr.title')
  return (
    <main id="main" className="pg ft-pg">
      {apps.isPending ? (
        <>
          <PageHead title={title} />
          <Loading />
        </>
      ) : apps.isError ? (
        <>
          <PageHead title={title} />
          <ErrorState error={apps.error} retry={() => apps.refetch()} />
        </>
      ) : app ? (
        <AppInstrumentation key={app.id} app={app} canEdit={canEdit} />
      ) : (
        <>
          <PageHead title={title} />
          <EmptyNote title={t('instr.pickApp')}>{t('instr.pickAppBody')}</EmptyNote>
        </>
      )}
    </main>
  )
}

function AppInstrumentation({ app, canEdit: role }: { app: App; canEdit: boolean | undefined }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ScopeSearch & { instrument?: string }
  const canEdit = role === true
  const policy = useQuery(instrumentationQuery(orgId, projectId, app.id))
  const layers = useQuery(layersQuery(orgId, projectId, app.id))
  const save = useSaveLayers(orgId, projectId, app.id)
  const kill = useSetKillSwitch(orgId, projectId, app.id)
  const who = useWho(orgId)
  const { toast, show } = useToast()
  // The rollouts edited and not published yet, by instrument: a draft, like Signals'.
  const [edits, setEdits] = useState<Partial<Record<InstrumentId, RolloutDraft>>>({})
  // A policy from the server (a publish, another tab's) restarts the draft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the layers changing is the trigger
  useEffect(() => setEdits({}), [layers.data])

  const held = layers.data?.layers ?? []
  const draft: Draft | undefined = policy.data ? toDraft(policy.data) : undefined
  const selected = (INSTRUMENTS.find((x) => x.id === search.instrument)?.id ??
    INSTRUMENTS.find((x) => draft && tileState(draft, held, x.id).kind === 'rolling')?.id ??
    'errors') as InstrumentId
  const select = (id: InstrumentId) =>
    void navigate({
      to: '.',
      search: (s: Record<string, unknown>) => ({ ...s, instrument: id }),
      replace: true,
    })

  const rolloutOf = (id: InstrumentId): RolloutDraft =>
    edits[id] ?? rolloutDraft(layerOf(held, id), id)
  const dirty = Object.keys(edits).length > 0
  const stateOf = (id: InstrumentId): TileState => {
    if (!draft) return { kind: 'off' }
    const e = edits[id]
    if (e) {
      if (e.percent <= 0)
        return tileState(draft, [], id).kind === 'on' ? { kind: 'on' } : { kind: 'off' }
      return e.percent >= 100 ? { kind: 'on' } : { kind: 'rolling', percent: e.percent }
    }
    return tileState(draft, held, id)
  }

  function publish() {
    let body = activeBodies(held)
    for (const [id, r] of Object.entries(edits) as [InstrumentId, RolloutDraft][])
      body = withRollout(body, id, t(NAME_KEY[id]), r)
    save.mutate(body, {
      onSuccess: () => show(t('instr.published')),
      // A layer in the list stopped since the page loaded (a guardrail, a teammate): the API
      // refuses to bring it back, so the list is read again before anything is published.
      onError: (e) => {
        if (layerStopped(e)) void layers.refetch()
      },
    })
  }

  const ks = policy.data?.kill_switch
  const stopped = ks?.on === true

  const meta = (
    <>
      {`${app.name} · ${PLATFORM_LABEL[app.platform]}`}
      {policy.data?.updated_at
        ? ` · ${changedLine(t, who(policy.data.updated_by), day(i18n, policy.data.updated_at))}`
        : null}
    </>
  )

  return (
    <>
      <PageHead
        title={t('instr.title')}
        meta={meta}
        actions={
          <>
            {canEdit && !stopped ? (
              <Confirm
                buttonClass="btn danger"
                confirmClass="btn danger solid"
                label={t('instr.kill')}
                title={t('instr.killTitle', { app: app.name })}
                body={t('instr.killBody')}
                confirm={t('instr.kill')}
                reason={{ label: t('instr.killReason'), maxLength: 500 }}
                busy={kill.isPending}
                onConfirm={(reason) =>
                  kill.mutate(
                    { on: true, ...(reason ? { reason } : {}) },
                    { onSuccess: () => show(t('instr.killed')) },
                  )
                }
              />
            ) : role === false && !stopped ? (
              <Locked label={t('instr.kill')} className="btn danger" />
            ) : null}
            <Btn variant="ghost" disabled={!dirty || save.isPending} onClick={() => setEdits({})}>
              {t('signals.discard')}
            </Btn>
            <Btn variant="solid" disabled={!canEdit || !dirty || save.isPending} onClick={publish}>
              {t('signals.publish')}
            </Btn>
          </>
        }
      />
      {role === false ? <p className="se-hint cr-note">{t('settings.adminOnly')}</p> : null}
      {save.error || kill.error ? (
        <p role="alert" className="se-hint cr-note cr-err">
          {layerStopped(save.error)
            ? t('instr.layerStopped')
            : isApiError(save.error ?? kill.error, 403)
              ? t('settings.adminOnly')
              : isApiError(save.error ?? kill.error, 400)
                ? t('signals.invalid')
                : t('state.failed')}
        </p>
      ) : null}
      {policy.isPending || layers.isPending || !draft ? (
        policy.isError || layers.isError ? (
          <ErrorState
            error={policy.error ?? layers.error}
            retry={() => {
              void policy.refetch()
              void layers.refetch()
            }}
          />
        ) : (
          <Loading />
        )
      ) : (
        <>
          <InstrumentStrip stateOf={stateOf} />
          <Section title={t('instr.device')} meta={t('instr.count', { count: INSTRUMENTS.length })}>
            <div className="ft-hero">
              <div className="ft-dia">
                <Device
                  app={app}
                  stateOf={stateOf}
                  selected={selected}
                  onSelect={select}
                  stopped={
                    stopped && ks
                      ? {
                          by: ks.by_apsio ? t('instr.byApsio') : who(ks.updated_by),
                          at: ks.updated_at,
                          byApsio: ks.by_apsio,
                        }
                      : undefined
                  }
                  canStart={canEdit && stopped && ks?.by_apsio !== true}
                  starting={kill.isPending}
                  onStart={() =>
                    kill.mutate({ on: false }, { onSuccess: () => show(t('instr.started')) })
                  }
                />
                <Legend />
                <p className="ft-rest">
                  {t('instr.rest', { count: INSTRUMENTS.length })}{' '}
                  <a className="ul" href={SDK_GUIDE[app.platform]} rel="noreferrer" target="_blank">
                    {t('instr.guide')}
                  </a>
                </p>
              </div>
              <Panel
                key={selected}
                app={app}
                id={selected}
                state={stateOf(selected)}
                layer={layerOf(held, selected)}
                layers={held}
                rollout={rolloutOf(selected)}
                edited={selected in edits}
                canEdit={canEdit}
                onChange={(r) => setEdits({ ...edits, [selected]: r })}
              />
            </div>
          </Section>
          {draft ? (
            <ByRelease
              app={app}
              draft={draft}
              layers={held}
              selected={selected}
              onSelect={select}
            />
          ) : null}
          <div className="grid2">
            <RecentChanges layers={held} />
            <Section title={t('instr.reach')} meta={t('instr.reachSub')}>
              <ol className="ft-flow">
                <li>
                  <b>{t('instr.reach.publish')}</b>
                  <span>{t('instr.reach.publishSub')}</span>
                </li>
                <li>
                  <b>{t('instr.reach.edge')}</b>
                  <span>{t('instr.reach.edgeSub')}</span>
                </li>
                <li>
                  <b>{t('instr.reach.fetch')}</b>
                  <span>{t('instr.reach.fetchSub')}</span>
                </li>
                <li>
                  <b>{t('instr.reach.devices')}</b>
                  <span>{t('instr.reach.devicesSub')}</span>
                </li>
              </ol>
            </Section>
          </div>
        </>
      )}
      {toast}
    </>
  )
}

/** The window Instruments by release reads releases from. */
const RELEASE_DAYS = 30

/**
 * Instruments by release (the mock's table under the device): what each instrument does on the
 * app's newest releases, from the base policy and the layers that target them, with each
 * release's share of the window's sessions.
 */
function ByRelease({
  app,
  draft,
  layers,
  selected,
  onSelect,
}: {
  app: App
  draft: Draft
  layers: readonly Layer[]
  selected: InstrumentId
  onSelect: (id: InstrumentId) => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { projectId } = useProjectParams()
  const releases = useQuery(
    releasesQuery(projectId, { app_id: app.id, ...lastDays(RELEASE_DAYS) }, [
      'instrumentation',
      app.id,
      RELEASE_DAYS,
    ]),
  )
  const cols = recentReleases(releases.data ?? [])
  return (
    <Section
      title={t('instr.byRelease')}
      meta={
        cols.length > 0
          ? t('instr.byReleaseSub', { count: cols.length, days: RELEASE_DAYS })
          : undefined
      }
    >
      {releases.isPending ? (
        <Loading />
      ) : releases.isError ? (
        <ErrorState error={releases.error} retry={() => releases.refetch()} />
      ) : cols.length === 0 ? (
        <EmptyNote title={t('instr.byReleaseNone')}>
          {t('instr.byReleaseNoneBody', { days: RELEASE_DAYS })}
        </EmptyNote>
      ) : (
        <div className="scroll-x">
          <table className="t ft-mx" aria-label={t('instr.byReleaseOf', { app: app.name })}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('instr.instrument')}
                </th>
                {cols.map((c) => (
                  <th key={c.release} className="ft-rc" scope="col">
                    <span translate="no">{c.release}</span>
                    <span className="t2">
                      {t('instr.ofSessions', { share: percentSpaced(i18n, c.share, 0) })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {INSTRUMENT_GROUPS.map((g) => (
                <ReleaseGroup
                  key={g}
                  group={g}
                  cols={cols}
                  cellOf={(id, release) => releaseCell(draft, layers, id, release)}
                  selected={selected}
                  onSelect={onSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function ReleaseGroup({
  group,
  cols,
  cellOf,
  selected,
  onSelect,
}: {
  group: InstrumentGroup
  cols: { release: string }[]
  cellOf: (id: InstrumentId, release: string) => ReleaseCell
  selected: InstrumentId
  onSelect: (id: InstrumentId) => void
}) {
  const { t } = useI18n()
  const rows = INSTRUMENTS.filter((x) => x.group === group)
  if (rows.length === 0) return null
  return (
    <>
      <tr className="grp">
        <td className="f" colSpan={cols.length + 1}>
          {t(GROUP_KEY[group])}
        </td>
      </tr>
      {rows.map((x) => (
        <tr key={x.id} className={x.id === selected ? 'ft-sel' : undefined}>
          <td className="f">
            <button
              type="button"
              className="ft-rn"
              aria-current={x.id === selected}
              onClick={() => onSelect(x.id)}
            >
              {t(NAME_KEY[x.id])}
            </button>
          </td>
          {cols.map((c) => {
            const cell = cellOf(x.id, c.release)
            const k = cellClass(cell)
            return (
              <td key={c.release}>
                <span className="ft-mc">
                  <i className={`ft-mb ${k}`} aria-hidden="true">
                    {cell.kind === 'rolling' ? <b style={{ width: `${cell.percent}%` }} /> : null}
                  </i>
                  <span className={`ft-mv ${k}`}>{cellWord(t, cell)}</span>
                </span>
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

const cellClass = (c: ReleaseCell): string =>
  c.kind === 'always' || c.kind === 'on'
    ? 'on'
    : c.kind === 'rolling'
      ? 'part'
      : c.kind === 'some'
        ? 'nt'
        : c.kind === 'rolled_back'
          ? 'kill'
          : 'off'

function cellWord(t: ReturnType<typeof useI18n>['t'], c: ReleaseCell): string {
  switch (c.kind) {
    case 'always':
      return t('instr.state.always')
    case 'on':
      return t('instr.state.on')
    case 'off':
      return t('instr.state.off')
    case 'rolling':
      return `${c.percent} %`
    case 'some':
      return t('instr.state.some')
    case 'rolled_back':
      return `× ${t('instr.state.rolledBack')}`
    case 'stopped':
      return `– ${t('instr.state.stopped')}`
  }
}

/** Mosaic's state words, the glyph decoration and the word for a screen reader. */
export function stateWord(t: ReturnType<typeof useI18n>['t'], s: TileState): string {
  switch (s.kind) {
    case 'always':
      return `◇ ${t('instr.state.always')}`
    case 'on':
      return `✓ ${t('instr.state.on')}`
    case 'off':
      return `– ${t('instr.state.off')}`
    case 'rolling':
      return `● ${s.percent} %`
    case 'rolled_back':
      return `× ${t('instr.state.rolledBack')}`
    case 'stopped':
      return `– ${t('instr.state.stopped')}`
    case 'expired':
      return `– ${t('instr.state.expired')}`
  }
}

const tileClass = (s: TileState): string =>
  s.kind === 'always' || s.kind === 'on'
    ? 'on'
    : s.kind === 'rolling'
      ? 'roll'
      : s.kind === 'rolled_back'
        ? 'kill'
        : 'off'

function InstrumentStrip({ stateOf }: { stateOf: (id: InstrumentId) => TileState }) {
  const { t } = useI18n()
  const states = INSTRUMENTS.map((x) => [x.id, stateOf(x.id)] as const)
  const on = states.filter(([, s]) => s.kind === 'on' || s.kind === 'always').length
  const rolling = states.filter(([, s]) => s.kind === 'rolling')
  const off = states.filter(([, s]) => ['off', 'stopped', 'expired'].includes(s.kind)).length
  const back = states.filter(([, s]) => s.kind === 'rolled_back').length
  return (
    <Strip
      label={t('instr.title')}
      items={[
        {
          dt: t('instr.strip.on'),
          dd: String(on),
          small: t('instr.strip.of', { n: INSTRUMENTS.length }),
          // What is not on, as the mock's sub says it: "2 off · 1 rolled back".
          d:
            [
              off > 0 ? t('instr.strip.off', { n: off }) : '',
              back > 0 ? t('instr.strip.back', { n: back }) : '',
            ]
              .filter(Boolean)
              .join(' · ') || undefined,
        },
        {
          dt: t('instr.strip.rolling'),
          dd: String(rolling.length),
          d:
            rolling.length > 0
              ? rolling
                  .map(([id, s]) => `${t(NAME_KEY[id])} ${s.kind === 'rolling' ? s.percent : 0} %`)
                  .join(' · ')
              : t('instr.strip.none'),
        },
      ]}
    />
  )
}

function Device({
  app,
  stateOf,
  selected,
  onSelect,
  stopped,
  canStart,
  starting,
  onStart,
}: {
  app: App
  stateOf: (id: InstrumentId) => TileState
  selected: InstrumentId
  onSelect: (id: InstrumentId) => void
  stopped: { by: string | undefined; at: string | null; byApsio: boolean } | undefined
  canStart: boolean
  starting: boolean
  onStart: () => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const ids = INSTRUMENTS.map((x) => x.id as InstrumentId)
  const move = (dir: number) => {
    const i = ids.indexOf(selected)
    const next = ids[(i + dir + ids.length) % ids.length]
    if (next) onSelect(next)
  }
  // brand v2 (design/v2/console Instrumentation): the instruments as a table, one row each,
  // selected to change it in the panel beside; the arrows still move between them.
  return (
    <div className="ft-list">
      {stopped ? (
        <p className="ft-stopped" role="status">
          {`! ${stopped.by ? t('instr.stoppedBy', { who: stopped.by }) : t('instr.stoppedAt')}${stopped.at ? ` · ${dayTime(i18n, stopped.at)}` : ''}`}
          {canStart ? (
            <Btn size="sm" disabled={starting} onClick={onStart}>
              {t('instr.start')}
            </Btn>
          ) : null}
        </p>
      ) : null}
      {/* biome-ignore lint/a11y/useSemanticElements: the instruments, moved with the arrows */}
      <div
        className={stopped ? 'ft-rows stop' : 'ft-rows'}
        role="group"
        aria-label={t('instr.tiles', { app: app.name })}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault()
            move(1)
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault()
            move(-1)
          }
        }}
      >
        {INSTRUMENT_GROUPS.map((g) => {
          const rows = INSTRUMENTS.filter((x) => x.group === g)
          if (rows.length === 0) return null
          const on = rows.filter((x) => {
            const k = stateOf(x.id).kind
            return k === 'on' || k === 'always'
          }).length
          return (
            <div key={g} className="ft-grp">
              <p className="ft-gh">
                <span>{t(GROUP_KEY[g])}</span>
                <span>{t('instr.groupOn', { on, n: rows.length })}</span>
              </p>
              {rows.map((x) => {
                const s = stateOf(x.id)
                const word = stateWord(t, s)
                return (
                  <button
                    key={x.id}
                    type="button"
                    className={`ft-row ${tileClass(s)}${'locked' in x ? ' lock' : ''}`}
                    aria-pressed={x.id === selected}
                    aria-label={`${t(NAME_KEY[x.id])}, ${word}`}
                    tabIndex={x.id === selected ? 0 : -1}
                    onClick={() => onSelect(x.id)}
                  >
                    <span className="ft-nm">{t(NAME_KEY[x.id])}</span>
                    <span className="ft-sv">
                      <i className={`ft-mini ${tileClass(s)}`} aria-hidden="true" />
                      {word}
                    </span>
                    <Icon name="right" small />
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Legend() {
  const { t } = useI18n()
  return (
    <ul className="ft-lg" aria-label={t('instr.legend')}>
      <li>
        <i className="ft-mini on" />
        <span>{t('instr.state.on')}</span>
      </li>
      <li>
        <i className="ft-mini roll" />
        <span>{t('instr.legend.rolling')}</span>
      </li>
      <li>
        <i className="ft-mini" />
        <span>{t('instr.state.off')}</span>
      </li>
      <li>
        <i className="ft-mini kill" />
        <span>{t('instr.state.rolledBack')}</span>
      </li>
    </ul>
  )
}

function Panel({
  app,
  id,
  state,
  layer,
  layers,
  rollout,
  edited,
  canEdit,
  onChange,
}: {
  app: App
  id: InstrumentId
  state: TileState
  layer: Layer | undefined
  layers: readonly Layer[]
  rollout: RolloutDraft
  edited: boolean
  canEdit: boolean
  onChange: (r: RolloutDraft) => void
}) {
  const { t } = useI18n()
  const sdk = resourcesSdk(t, app.platform, PLATFORM_LABEL[app.platform])
  // The app's SDK does not record the instrument yet: its state cannot change (APS-462).
  const notYet =
    id === 'resources' && sdk.since === null
      ? t('instr.resourcesNotYet', { platform: PLATFORM_LABEL[app.platform] })
      : undefined
  const { orgId, projectId } = useProjectParams()
  const [tab, setTab] = useState<Tab>('rollout')
  const x = INSTRUMENTS.find((i) => i.id === id)
  const locked = x !== undefined && 'locked' in x
  const active = layer?.state === 'active' ? layer : undefined
  const comparison = useQuery({
    ...layerComparisonQuery(orgId, projectId, app.id, active?.id ?? ''),
    enabled: active !== undefined,
  })
  const hasCost = comparison.data !== undefined && comparison.data !== null
  const tabs: Tab[] = locked
    ? ['rollout']
    : (['rollout', ...(hasCost ? (['cost'] as const) : []), 'settings', 'history'] as Tab[])
  const group = x?.group
  return (
    <div className="ft-pnw">
      <div className="ft-pn">
        <div className="ft-p0">
          <p className="ft-pk">
            {group ? t(GROUP_KEY[group]) : null}
            {x && 'family' in x ? (
              <>
                {' · '}
                <code translate="no">{`families.${x.family}`}</code>
              </>
            ) : null}
          </p>
          <h2 className="ft-pt">{t(NAME_KEY[id])}</h2>
          <p className="ft-what">{t(WHAT_KEY[id])}</p>
          {id === 'resources' && sdk.since !== null ? <p className="ft-sdk">{sdk.line}</p> : null}
          {x && 'family' in x ? (
            <div className="ft-facts">
              <span>{`${t(FAMILY_KEY[x.family])}: ${stateWord(t, state)}`}</span>
              <Link
                className="lk"
                to="/o/$orgId/p/$projectId/signals"
                params={{ orgId, projectId }}
                search={{ app: app.id }}
              >
                {`${t('signals.title')} →`}
              </Link>
            </div>
          ) : null}
        </div>
        <div className="tabs ft-tabs" role="tablist" aria-label={t(NAME_KEY[id])}>
          {tabs.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={k === tab}
              onClick={() => setTab(k)}
            >
              {t(`instr.tab.${k}` as MessageId)}
            </button>
          ))}
        </div>
        {tab === 'rollout' ? (
          locked ? (
            <Section title={t('instr.stateTitle')} meta={t('instr.lockedSub')}>
              <p className="ft-nil">{t('instr.lockedBody')}</p>
            </Section>
          ) : (
            <>
              <RolloutState
                notYet={notYet}
                id={id}
                state={state}
                rollout={rollout}
                layer={active}
                canEdit={canEdit}
                edited={edited}
                onChange={onChange}
              />
              {/* No rollout can start where the SDK does not record the instrument: no editors for one. */}
              {notYet === undefined ? (
                <>
                  <Targeting id={id} rollout={rollout} canEdit={canEdit} onChange={onChange} />
                  <Guardrails
                    rollout={rollout}
                    layer={layer}
                    canEdit={canEdit}
                    onChange={onChange}
                  />
                </>
              ) : null}
            </>
          )
        ) : tab === 'cost' && comparison.data ? (
          <Section title={t('instr.costTitle')} meta={t('instr.costSub')}>
            <Cost data={comparison.data} />
          </Section>
        ) : tab === 'settings' ? (
          <Settings id={id} rollout={rollout} canEdit={canEdit} onChange={onChange} />
        ) : tab === 'history' ? (
          <History id={id} layers={layers} />
        ) : null}
      </div>
    </div>
  )
}

function RolloutState({
  notYet,
  id,
  state,
  rollout,
  layer,
  canEdit,
  edited,
  onChange,
}: {
  /** Why the state cannot change: the app's SDK does not record the instrument yet. */
  notYet?: string | undefined
  id: InstrumentId
  state: TileState
  rollout: RolloutDraft
  layer: Layer | undefined
  canEdit: boolean
  edited: boolean
  onChange: (r: RolloutDraft) => void
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const stop = useStopLayer(orgId, projectId, search.app ?? '')
  const holdId = useId()
  const kind = rollout.percent <= 0 ? 'off' : rollout.percent >= 100 ? 'on' : ('rolling' as const)
  const setPercent = (percent: number) => onChange({ ...rollout, percent })
  return (
    <Section title={t('instr.stateTitle')} meta={t('instr.stateSub')}>
      <div className="ft-strow">
        <Seg
          label={t('instr.stateOf', { name: t(NAME_KEY[id]) })}
          options={[
            { value: 'off', label: t('instr.seg.off') },
            { value: 'rolling', label: t('instr.seg.rolling') },
            { value: 'on', label: t('instr.seg.on') },
          ]}
          value={kind}
          onChange={(v) => setPercent(v === 'off' ? 0 : v === 'on' ? 100 : ROLLOUT_STOPS[0])}
          disabled={!canEdit || notYet !== undefined}
        />
        {canEdit && layer && !edited ? (
          <Confirm
            buttonClass="btn danger sm ft-kb"
            confirmClass="btn danger solid"
            label={t('instr.stopLayer')}
            title={t('instr.stopLayerTitle', { name: t(NAME_KEY[id]) })}
            body={t('instr.stopLayerBody')}
            confirm={t('instr.stopLayer')}
            reason={{ label: t('instr.killReason'), maxLength: 500 }}
            busy={stop.isPending}
            onConfirm={(reason) => stop.mutate({ layerId: layer.id, reason: reason ?? '' })}
          />
        ) : null}
      </div>
      {notYet ? <p className="ft-sdk">{notYet}</p> : null}
      {state.kind === 'rolled_back' || state.kind === 'stopped' ? (
        <p className="ft-kc" role="status">
          {`${stateWord(t, state)}${state.reason ? ` · ${state.reason}` : ''}`}
        </p>
      ) : null}
      {kind === 'rolling' ? (
        <>
          <p className="ft-reach">
            <b>{`${rollout.percent} %`}</b>
            {t('instr.reachText', { held: rollout.holdbackPercent })}
          </p>
          <div
            className="ft-trk"
            aria-hidden="true"
            style={{ '--x': `${rollout.percent}%` } as CSSProperties}
          >
            <span className="ft-rail" />
            <span className="ft-fil" />
            <span className="ft-thb" />
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: the rollout's stops, one pressed */}
          <div className="ft-stops" role="group" aria-label={t('instr.stops')}>
            {ROLLOUT_STOPS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={p === rollout.percent}
                disabled={!canEdit}
                onClick={() => setPercent(p)}
              >
                {`${p} %`}
              </button>
            ))}
          </div>
        </>
      ) : null}
      {kind !== 'off' ? (
        <label className="lbl ft-hold" htmlFor={holdId}>
          {t('instr.holdback')}
          <span className="cr-wm">
            <input
              id={holdId}
              className="input mono"
              type="number"
              inputMode="decimal"
              min={0}
              max={HOLDBACK_MAX}
              step={1}
              disabled={!canEdit}
              value={rollout.holdbackPercent}
              onChange={(e) =>
                onChange({
                  ...rollout,
                  holdbackPercent: Math.min(HOLDBACK_MAX, Math.max(0, Number(e.target.value))),
                })
              }
            />
            <span>{t('instr.holdbackSub')}</span>
          </span>
        </label>
      ) : null}
    </Section>
  )
}

type WhenKey =
  | 'app_version'
  | 'os_name'
  | 'os_version_min'
  | 'os_version_max'
  | 'device_model'
  | 'installs'
  | 'users'
const WHEN_KEYS: readonly WhenKey[] = [
  'app_version',
  'os_name',
  'os_version_min',
  'os_version_max',
  'device_model',
  'installs',
  'users',
]
const LIST_KEYS = new Set<WhenKey>(['app_version', 'os_name', 'device_model', 'installs', 'users'])

function Targeting({
  id,
  rollout,
  canEdit,
  onChange,
}: {
  id: InstrumentId
  rollout: RolloutDraft
  canEdit: boolean
  onChange: (r: RolloutDraft) => void
}) {
  const { t } = useI18n()
  const [key, setKey] = useState<WhenKey>('app_version')
  const [value, setValue] = useState('')
  const [adding, setAdding] = useState(false)
  const when = rollout.when
  const chips = WHEN_KEYS.flatMap((k) => {
    const v = when[k]
    if (v === undefined) return []
    return (Array.isArray(v) ? v : [v]).map((one) => ({ k, one }))
  })
  // A layer's stored installs and users are hints the API cannot take back: changing them starts
  // from none, and the person types each id again.
  const stored = rollout.subjects === 'kept' && hasSubjects(when)
  const subjectsFrom = (k: WhenKey): RolloutDraft['when'] =>
    (k === 'installs' || k === 'users') && stored ? withoutSubjects(when) : when
  const touch = (k: WhenKey, next: RolloutDraft['when']) =>
    onChange({
      ...rollout,
      when: next,
      ...(k === 'installs' || k === 'users' ? { subjects: 'replaced' as const } : {}),
    })
  const remove = (k: WhenKey, one: string) => {
    if ((k === 'installs' || k === 'users') && stored) return touch(k, withoutSubjects(when))
    const v = when[k]
    const rest = Array.isArray(v) ? v.filter((x) => x !== one) : []
    const { [k]: _, ...others } = when
    touch(k, rest.length > 0 ? { ...others, [k]: rest } : others)
  }
  const add = () => {
    const v = value.trim()
    if (!v) return
    const from = subjectsFrom(key)
    const cur = from[key]
    touch(key, {
      ...from,
      [key]: LIST_KEYS.has(key) ? [...new Set([...(Array.isArray(cur) ? cur : []), v])] : v,
    })
    setValue('')
    setAdding(false)
  }
  const shown = (k: WhenKey, one: string) => (k === 'installs' || k === 'users' ? idHint(one) : one)
  return (
    <Section title={t('instr.targeting')} meta={t('instr.targetingSub')}>
      <div className="ft-rule">
        {chips.map(({ k, one }) => (
          <span key={`${k}:${one}`} className="ft-chip" title={one}>
            <span translate="no">
              <span className="ft-cf">{t(`instr.when.${k}` as MessageId)}</span>{' '}
              <b>{shown(k, one)}</b>
            </span>
            {canEdit ? (
              <button
                type="button"
                aria-label={t('instr.removeCondition', {
                  what: `${t(`instr.when.${k}` as MessageId)} ${shown(k, one)}`,
                  name: t(NAME_KEY[id]),
                })}
                onClick={() => remove(k, one)}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        <span className="ft-chip ft-cp">
          <b>{chips.length === 0 ? t('instr.everyInstall') : t('instr.theseInstalls')}</b>
        </span>
        {canEdit ? (
          adding ? (
            <span className="ft-add">
              <select
                className="select"
                aria-label={t('instr.conditionField')}
                value={key}
                onChange={(e) => setKey(e.target.value as WhenKey)}
              >
                {WHEN_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {t(`instr.when.${k}` as MessageId)}
                  </option>
                ))}
              </select>
              <input
                className="input mono"
                aria-label={t('instr.conditionValue')}
                translate="no"
                maxLength={128}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    add()
                  }
                  if (e.key === 'Escape') setAdding(false)
                }}
              />
              <Btn size="sm" disabled={!value.trim()} onClick={add}>
                {t('instr.add')}
              </Btn>
            </span>
          ) : (
            <button type="button" className="ft-chip add" onClick={() => setAdding(true)}>
              {`+ ${t('rules.condition')}`}
            </button>
          )
        ) : null}
      </div>
      {canEdit && stored ? <p className="note">{t('instr.subjectsKept')}</p> : null}
    </Section>
  )
}

/** The API refused a publish naming a layer that stopped since the page read the list (409). */
const layerStopped = (e: unknown) => isApiError(e, 409) && e.code === 'layer_stopped'

function Guardrails({
  rollout,
  layer,
  canEdit,
  onChange,
}: {
  rollout: RolloutDraft
  layer: Layer | undefined
  canEdit: boolean
  onChange: (r: RolloutDraft) => void
}) {
  const { t } = useI18n()
  const g = rollout.guardrails
  // A guardrail compares the devices that took the layer with its holdback: none, nothing to watch.
  const can = canEdit && rollout.holdbackPercent > 0
  const tripped =
    layer?.state === 'rolled_back' && layer.stopped_reason?.startsWith('guardrail:')
      ? layer.stopped_reason.slice('guardrail:'.length).trim()
      : undefined
  const set = (patch: Partial<RolloutDraft['guardrails']>) =>
    onChange({ ...rollout, guardrails: { ...g, ...patch } })
  const row = (
    key: 'crash_free_drop_points' | 'ingest_increase_pct',
    label: string,
    unit: string,
    step: number,
  ) => {
    const on = g[key] !== undefined
    return (
      <div className={on ? 'ft-gr' : 'ft-gr off'}>
        <button
          type="button"
          className="ft-sw"
          role="switch"
          aria-checked={on}
          aria-label={t('instr.guardOn', { what: label })}
          disabled={!can}
          onClick={() => {
            const { [key]: _, ...rest } = g
            onChange({
              ...rollout,
              guardrails: on ? rest : { ...g, [key]: step === 0.1 ? 0.1 : 10 },
            })
          }}
        />
        <span className="ft-gt">{label}</span>
        {on ? (
          <span className="ft-gv">
            <input
              className="input mono"
              type="number"
              inputMode="decimal"
              aria-label={t('instr.guardBound', { what: label })}
              min={step}
              step={step}
              disabled={!can}
              value={g[key]}
              onChange={(e) => set({ [key]: Number(e.target.value) })}
            />
            <small>{unit}</small>
          </span>
        ) : (
          <span className="ft-gv" />
        )}
      </div>
    )
  }
  return (
    <Section title={t('instr.guardrails')} meta={t('instr.guardrailsSub')}>
      {row('crash_free_drop_points', t('instr.guard.crashFree'), t('instr.guard.points'), 0.1)}
      {row('ingest_increase_pct', t('instr.guard.ingest'), '%', 1)}
      <dl className="kv ft-ms">
        <dt>{t('instr.guard.minSessions')}</dt>
        <dd>
          <input
            className="input mono"
            type="number"
            inputMode="numeric"
            aria-label={t('instr.guard.minSessions')}
            min={1}
            disabled={!can}
            value={g.min_sessions ?? 200}
            onChange={(e) => set({ min_sessions: Math.max(1, Math.round(Number(e.target.value))) })}
          />
        </dd>
      </dl>
      {tripped ? (
        <p className="ft-kc" role="status">{`× ${t('instr.tripped', { which: tripped })}`}</p>
      ) : rollout.holdbackPercent <= 0 ? (
        <p className="note ft-gn">{t('instr.guardNeedsHoldback')}</p>
      ) : (
        <p className="note ft-gn">{t('instr.guardNote')}</p>
      )}
    </Section>
  )
}

function Cost({ data }: { data: LayerComparison }) {
  const i18n = useI18n()
  const { t } = i18n
  const rows: [string, string, string][] = [
    [t('instr.cost.sessions'), String(data.holdback.sessions), String(data.applied.sessions)],
    [
      t('instr.cost.crashFree'),
      percentSpaced(i18n, data.holdback.crash_free_sessions, 2),
      percentSpaced(i18n, data.applied.crash_free_sessions, 2),
    ],
    [
      t('instr.cost.bytes'),
      bytes(data.holdback.ingest_bytes_per_session),
      bytes(data.applied.ingest_bytes_per_session),
    ],
  ]
  return (
    <>
      <table className="t">
        <thead>
          <tr>
            <th scope="col">{t('instr.cost.metric')}</th>
            <th scope="col" className="num">
              {t('instr.cost.without')}
            </th>
            <th scope="col" className="num">
              {t('instr.cost.with')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([n, wo, w]) => (
            <tr key={n}>
              <td>{n}</td>
              <td className="num">{wo}</td>
              <td className="num">{w}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="note">{t('instr.cost.since', { date: day(i18n, data.since) })}</p>
    </>
  )
}

const bytes = (n: number) =>
  n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`

function Settings({
  id,
  rollout,
  canEdit,
  onChange,
}: {
  id: InstrumentId
  rollout: RolloutDraft
  canEdit: boolean
  onChange: (r: RolloutDraft) => void
}) {
  const { t } = useI18n()
  const x = INSTRUMENTS.find((i) => i.id === id)
  const s = rollout.settings
  const capture = x && 'capture' in x ? x.capture : undefined
  return (
    <Section title={t('instr.settings')} meta={t('instr.settingsSub')}>
      <div className="ft-cfg">
        <div>
          <b>{t('instr.set.sends')}</b>
          <span className="ft-h">{t('instr.set.sendsSub')}</span>
        </div>
        <Seg
          label={t('instr.set.sends')}
          options={[
            { value: 'always', label: t('signals.mode.always') },
            { value: 'sampled', label: t('signals.mode.sampled') },
          ]}
          value={s.family.mode === 'sampled' ? 'sampled' : 'always'}
          onChange={(mode) =>
            onChange({
              ...rollout,
              settings: {
                ...s,
                family: mode === 'sampled' ? { mode, rate: s.family.rate ?? 0.1 } : { mode },
              },
            })
          }
          disabled={!canEdit}
        />
      </div>
      {capture ? (
        <div className="ft-cfg">
          <div>
            <b>{t('instr.set.collects')}</b>
            <span className="ft-h">{t('instr.set.collectsSub')}</span>
          </div>
          <Seg
            label={t('instr.set.collects')}
            options={[
              { value: 'app', label: t('instr.set.asApp') },
              { value: 'off', label: t('instr.seg.off') },
            ]}
            value={s.capture === undefined ? 'app' : 'off'}
            onChange={(v) => {
              const { capture: _, ...rest } = s
              onChange({
                ...rollout,
                settings:
                  v === 'app'
                    ? rest
                    : { ...rest, capture: capture === 'logs_min_severity' ? 'FATAL' : false },
              })
            }}
            disabled={!canEdit}
          />
        </div>
      ) : null}
    </Section>
  )
}

function History({ id, layers }: { id: InstrumentId; layers: readonly Layer[] }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId } = useProjectParams()
  const who = useWho(orgId)
  const mine = layers.filter((l) => instrumentsOf(l).includes(id))
  return (
    <Section title={t('instr.history')} meta={t('instr.historySub')}>
      {mine.length === 0 ? (
        <p className="ft-nil">{t('instr.historyNone')}</p>
      ) : (
        <ul className="rl ft-log">
          {mine.map((l) => (
            <li key={l.id}>
              <span>
                {l.state === 'active'
                  ? `● ${l.rollout?.percent ?? 100} %`
                  : `${l.state === 'rolled_back' ? '×' : '–'} ${t(`instr.state.${l.state === 'rolled_back' ? 'rolledBack' : l.state}` as MessageId)}`}
                {l.stopped_reason ? ` · ${l.stopped_reason}` : null}
              </span>
              <span>
                {[
                  l.updated_by === null && l.state === 'rolled_back'
                    ? t('instr.guardrail')
                    : who(l.updated_by),
                  l.updated_at ? dayTime(i18n, l.updated_at) : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

function RecentChanges({ layers }: { layers: readonly Layer[] }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const who = useWho(orgId)
  const recent = [...layers]
    .filter((l) => l.updated_at)
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
    .slice(0, 8)
  return (
    <Section
      title={t('instr.recent')}
      meta={t('instr.recentSub')}
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'activity' }}
        >
          {t('instr.auditLog')}
        </Link>
      }
    >
      {recent.length === 0 ? (
        <p className="ft-nil">{t('instr.recentNone')}</p>
      ) : (
        <ul className="rl ft-log">
          {recent.map((l) => (
            <li key={l.id}>
              <span>
                {`${l.name} · `}
                {l.state === 'active'
                  ? `● ${l.rollout?.percent ?? 100} %`
                  : t(
                      `instr.state.${l.state === 'rolled_back' ? 'rolledBack' : l.state}` as MessageId,
                    )}
              </span>
              <span>
                {[
                  l.updated_by === null && l.state === 'rolled_back'
                    ? t('instr.guardrail')
                    : who(l.updated_by),
                  l.updated_at ? day(i18n, l.updated_at) : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
